import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { t, getMonths, formatPeriod } from '../../utils/creatorLang';
import { useCreatorAuth } from '../../utils/useCreatorAuth';
import {
  CreatorScreen, Card, Pill, BottomNav, ScreenHeader,
  I, SectionLabel, StatTile, DiamondGem, TierBadge,
} from '../../components/creator/primitives';

const fmtShort = (n) => {
  if (n == null) return '0';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'k';
  return String(Math.round(n));
};

const CreatorStats = () => {
  const navigate = useNavigate();
  const { tiktokId: authTiktokId, loading: authLoading, isDebugMode } = useCreatorAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [monthlyData, setMonthlyData] = useState([]);

  useEffect(() => {
    if (authLoading || !authTiktokId) return;
    const init = async () => {
      setLoading(true);
      try {
        const [{ data: history }, { data: current }] = await Promise.all([
          supabase
            .from('black_creator_monthly')
            .select('period, diamonds, live_days, live_hours, tier_p, activeness_level, tier_evolution, cashback, total_bonus, net_agency')
            .eq('tiktok_id', authTiktokId)
            .order('period', { ascending: false }),
          supabase
            .from('black_creator_current_month')
            .select('period, diamonds, live_days, live_duration_minutes, tier_current, tier_m1, cashback_amount')
            .eq('tiktok_id', authTiktokId)
            .maybeSingle(),
        ]);

        const rows = history ? [...history] : [];

        if (current?.period && !rows.some((r) => r.period === current.period)) {
          const tierEvo = current.tier_current && current.tier_m1
            ? (current.tier_current > current.tier_m1 ? 'up' : current.tier_current < current.tier_m1 ? 'down' : null)
            : null;
          rows.unshift({
            period: current.period,
            diamonds: current.diamonds || 0,
            live_days: current.live_days || 0,
            live_hours: Math.round(((current.live_duration_minutes || 0) / 60) * 10) / 10,
            tier_p: current.tier_current || 1,
            tier_evolution: tierEvo,
            cashback: current.cashback_amount || 0,
            is_current: true,
          });
        }

        setMonthlyData(rows);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [authTiktokId, authLoading]);

  const chartData = useMemo(() => {
    return [...monthlyData].sort((a, b) => a.period?.localeCompare(b.period)).slice(-12);
  }, [monthlyData]);

  const maxDiamonds = useMemo(() => Math.max(...chartData.map(d => d.diamonds || 0), 1), [chartData]);
  const bestPeriod = useMemo(() => {
    if (!chartData.length) return null;
    return chartData.reduce((a, b) => (b.diamonds || 0) > (a.diamonds || 0) ? b : a, chartData[0]);
  }, [chartData]);

  const summary = useMemo(() => {
    if (!monthlyData.length) return null;
    const totalDiamonds = monthlyData.reduce((s, r) => s + (r.diamonds || 0), 0);
    const totalMonths = monthlyData.length;
    const avgDiamonds = Math.round(totalDiamonds / totalMonths);
    const bestMonth = [...monthlyData].sort((a, b) => (b.diamonds || 0) - (a.diamonds || 0))[0];
    return { totalDiamonds, totalMonths, avgDiamonds, bestMonth };
  }, [monthlyData]);

  const qs = isDebugMode && authTiktokId ? `?creator=${authTiktokId}` : '';

  if (loading) {
    return (
      <CreatorScreen>
        <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }}>
          <div style={{
            width: 32, height: 32,
            border: '2px solid var(--accent-pink)', borderTopColor: 'transparent',
            borderRadius: '50%', animation: 'spin 0.8s linear infinite',
          }}/>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </CreatorScreen>
    );
  }

  const months = getMonths();

  return (
    <CreatorScreen>
      <div className="screen grain">
        <ScreenHeader
          title={t('statistics')}
          subtitle={`${monthlyData.length} ${t('months')}`}
          onBack={() => navigate(`/my/dashboard${qs}`)}
        />

        {/* Summary tiles */}
        {summary && (
          <div style={{ padding: '0 20px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <StatTile
              label={t('total_diamonds')}
              value={fmtShort(summary.totalDiamonds)}
              icon={<DiamondGem size={12} color="var(--accent-cyan)"/>}
            />
            <StatTile
              label={t('avg_per_month')}
              value={fmtShort(summary.avgDiamonds)}
              icon={<I.TrendUp size={12} stroke="var(--accent-cyan)"/>}
            />
            <StatTile
              label={t('best_month')}
              value={fmtShort(summary.bestMonth?.diamonds)}
              sub={formatPeriod(summary.bestMonth?.period)?.split(' ')[0]}
              icon={<I.Sparkle size={12} stroke="var(--accent-gold)"/>}
            />
          </div>
        )}

        {/* Monthly chart */}
        <div style={{ padding: '20px 20px 0' }}>
          <Card pad={16}>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 10, color: 'var(--fg-3)', fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                  {t('diamonds_per_month')}
                </div>
                {summary && (
                  <div className="mono tab" style={{ fontSize: 24, fontWeight: 800, letterSpacing: -0.8, marginTop: 4, display: 'flex', alignItems: 'baseline', gap: 6, color: 'var(--fg-0)' }}>
                    {fmtShort(summary.totalDiamonds)}
                    <DiamondGem size={14} color="var(--accent-cyan)"/>
                  </div>
                )}
              </div>
              {chartData.length >= 2 && (() => {
                const prev = chartData[chartData.length - 2]?.diamonds || 0;
                const curr = chartData[chartData.length - 1]?.diamonds || 0;
                if (prev === 0) return null;
                const pct = Math.round(((curr - prev) / prev) * 100);
                return <Pill color={pct >= 0 ? 'green' : 'red'}>{pct >= 0 ? '+' : ''}{pct}% vs M-1</Pill>;
              })()}
            </div>

            {chartData.length === 0 ? (
              <div style={{ height: 120, display: 'grid', placeItems: 'center', color: 'var(--fg-3)', fontSize: 12 }}>
                {t('no_data')}
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 90 }}>
                  {chartData.map((d, i) => {
                    const pct = maxDiamonds > 0 ? ((d.diamonds || 0) / maxDiamonds) * 100 : 0;
                    const isBest = d.period === bestPeriod?.period;
                    return (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                        <div style={{
                          width: '100%', maxWidth: 28,
                          height: `${Math.max(pct, 4)}%`, minHeight: 4,
                          background: isBest
                            ? 'linear-gradient(180deg, var(--accent-gold), oklch(0.82 0.14 85 / 0.4))'
                            : 'linear-gradient(180deg, var(--accent-pink), oklch(0.68 0.22 10 / 0.3))',
                          borderRadius: 3,
                          opacity: isBest ? 1 : 0.7,
                        }}/>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                  {chartData.map((d, i) => {
                    const month = d.period?.split('-')[1];
                    const monthLabel = month ? months[parseInt(month, 10) - 1] : '';
                    return (
                      <div key={i} style={{ flex: 1, textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--fg-3)' }}>
                        {monthLabel}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </Card>
        </div>

        {/* Monthly history */}
        <div style={{ padding: '24px 20px 0' }}>
          <SectionLabel>{t('monthly_history')}</SectionLabel>

          {monthlyData.length === 0 ? (
            <Card pad={30} style={{ textAlign: 'center', color: 'var(--fg-3)', fontSize: 13 }}>
              {t('no_data')}
            </Card>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {monthlyData.map((row, i) => {
                const tierEvo = row.tier_evolution;
                const evoLabel = tierEvo === 'up' ? '↑' : tierEvo === 'down' ? '↓' : '→';
                const evoColor = tierEvo === 'up' ? 'var(--accent-green)' : tierEvo === 'down' ? 'var(--accent-red)' : 'var(--fg-3)';
                return (
                  <Card key={i} pad={14} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <TierBadge tier={row.tier_p || 1} size="sm"/>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-0)' }}>
                          {formatPeriod(row.period)}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: evoColor }}>{evoLabel}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--fg-3)' }}>
                        <span>{row.live_days ?? '—'} {t('days_label')}</span>
                        <span>{row.live_hours ? `${row.live_hours}h` : '—'}</span>
                        {row.cashback > 0 && (
                          <span style={{ color: 'var(--accent-green)' }}>
                            {fmtShort(row.cashback)} cashback
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div className="mono tab" style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent-cyan)' }}>
                        {fmtShort(row.diamonds)}
                      </div>
                      <div style={{ fontSize: 9, color: 'var(--fg-3)' }}>{t('diamonds').toLowerCase()}</div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <BottomNav active="stats" qs={qs}/>
    </CreatorScreen>
  );
};

export default CreatorStats;
